
import { functions } from '../../firebaseClient';
import type { AttachmentMeta } from "../types/attachments";

// Shared Interfaces
export interface AIAnalysisRequest {
  fileMeta?: AttachmentMeta;  // Single file (Legacy support)
  files?: AttachmentMeta[];   // Multiple files (Preferred)
  purpose?: string;           // e.g. "contract_review", "seal_request"
  mode?: 'summary' | 'analysis' | 'risk' | 'decision' | 'command' | 'draft';
  userInstruction?: string;   // User prompt
  contextType?: string;
  contextId?: string;         // General Context ID
  contractId?: string;        // Explicit Contract ID for DB lookup
  extraContext?: Record<string, any>;
}

export interface AIAnalysisResponse {
  summary: string;
  result?: any;
  debug?: { traceId: string };
  ok: boolean;
}

// Internal Wrapper for Callable
async function callCloudFunction(name: string, payload: any): Promise<AIAnalysisResponse> {
    try {
        const fn = functions.httpsCallable(name);
        const res = await fn(payload);
        return res.data as AIAnalysisResponse;
    } catch (e: any) {
        console.error("[AI Service] Error:", e);
        
        // Extract real message from Firebase Error
        const msg = e.message || e.details?.message || JSON.stringify(e);
        
        if (msg.includes("unauthenticated")) {
            throw new Error("請先登入後再使用。");
        }
        // Pass through specific errors
        throw new Error(msg);
    }
}

/**
 * Unified entry point for analyzing uploaded files
 */
export async function analyzeUploadedFile(params: AIAnalysisRequest): Promise<any> {
    // 1. Prepare Attachments (Normalize to Array)
    const attachments = [];
    
    if (params.files && params.files.length > 0) {
        attachments.push(...params.files.map(f => ({ 
            name: f.name || "unknown_file", 
            path: f.path || f.storagePath || "",
            url: f.url,
            contentType: f.contentType
        })));
    } 
    
    if (params.fileMeta) {
        attachments.push({ 
            name: params.fileMeta.name || "unknown_file", 
            path: params.fileMeta.path || params.fileMeta.storagePath || "",
            url: params.fileMeta.url,
            contentType: params.fileMeta.contentType
        });
    }

    // 2. Validate Inputs Client-Side
    // Allow if instruction exists OR if attachments exist OR if contractId provided
    if (attachments.length === 0 && !params.userInstruction && !params.contractId) {
        throw new Error("請提供附件、合約ID 或輸入指令。");
    }

    // 3. Determine Context
    let contextType = params.contextType || "GENERAL";
    if (params.purpose?.includes("contract")) contextType = "CONTRACT";
    if (params.purpose?.includes("seal")) contextType = "SEAL";

    // Grab first file info for top-level fallback
    const f0 = attachments[0];

    console.log("[AI Service] Sending Payload:", {
        fileName: f0?.name,
        hasUrl: !!f0?.url,
        contractId: params.contractId
    });

    // 4. Call Backend
    const response = await callCloudFunction('analyzeContractAttachment', {
        // Array for multi-file processing
        attachments: attachments,
        files: attachments, // Alias

        // Explicit Top-level fields to satisfy backend validation
        fileName: f0?.name || "",
        fileUrl: f0?.url || "",
        filePath: f0?.path || "",
        contentType: f0?.contentType || "",
        
        // Pass other fields
        contractId: params.contractId || null, 
        purpose: params.purpose,
        mode: params.mode || 'summary',
        userInstruction: params.userInstruction || "",
        contextType,
        contextId: params.contextId
    });

    // 5. Return result
    if (response.result) {
        return { ...response.result, _debug: response.debug };
    }
    return { 
        summary: response.summary, 
        description: response.summary, 
        _debug: response.debug 
    };
}

/**
 * Legacy wrapper for Assistant Drawer
 */
export async function callLegalAssistant(payload: any): Promise<{ ok: boolean, text: string, message?: string }> {
    try {
        // Map legacy payload to new structure
        const mappedFiles = (payload.attachments || []).map((a: any) => ({
            name: a.name,
            storagePath: a.storagePath || a.path,
            url: a.downloadUrl || a.url,
            contentType: a.contentType,
            size: a.size
        }));

        const res = await analyzeUploadedFile({
            files: mappedFiles,
            // If legacy single file provided
            fileMeta: payload.attachmentPath ? { name: payload.fileName, path: payload.attachmentPath } as any : undefined,
            mode: payload.mode,
            contextType: payload.contextType,
            contextId: payload.contextId,
            userInstruction: payload.userInstruction
        });

        return { ok: true, text: res.summary || JSON.stringify(res) };
    } catch (e: any) {
        return { ok: false, text: "", message: e.message };
    }
}
